import { DriverStatus, UserValidationGroups } from "@daytrip/legacy-enums";
import { DocumentSubject } from "@daytrip/legacy-enums";
import { SimpleCountry } from "@daytrip/legacy-models";
import { Document } from "@daytrip/legacy-models";
import { DocumentType } from "@daytrip/legacy-models";
import { Driver } from "@daytrip/legacy-models";
import { User } from "@daytrip/legacy-models";
import { isNotUndefinedOrNull, isUndefinedOrNull } from "@daytrip/utils";
import autobind from "autobind-decorator";
import { plainToClass } from "class-transformer";
import { action, computed, observable } from "mobx";

import { globalManagementLogger } from "../../global-logger";
import { CompanyDriverOperator } from "../../operators/CompanyDriverOperator";
import { DocumentOperator } from "../../operators/DocumentOperator";
import { DocumentTypeOperator } from "../../operators/DocumentTypeOperator";
import { PageStore } from "../../stores/PageStore";
import { throwIfInvalidUserPhoneNumber } from "../../utils/validateUserPhoneNumber";

import { CompanyDriversPageRouter } from "./CompanyDriversPageRouter";

@autobind
export class CompanyDriversPageStore extends PageStore<CompanyDriversPageRouter, {}> {
    @observable
    public driversCompany: User;

    @observable
    public companyDriverOperators: Array<CompanyDriverOperator>;

    @observable
    public driversCount: number = 0;

    @observable
    public simpleCountries: Array<SimpleCountry>;

    @observable
    public existingUser?: boolean;

    @computed
    public get country(): SimpleCountry | undefined {
        return this.simpleCountries?.find((c) => c._id === this.driversCompany?.countryId);
    }

    @action
    public async validateEmail(operator: CompanyDriverOperator) {
        if (
            operator.editedModel &&
            operator.editedModel.email &&
            (await this.rpcClient.user.checkIfUserWithEmailExists(operator.editedModel.email, operator.editedModel._id))
        ) {
            this.existingUser = true;
        } else {
            this.existingUser = undefined;
        }
    }

    @action
    public addDriver(): void {
        const newUser = new User();
        newUser.driver = new Driver();
        newUser.driver.driversCompanyUserId = this.driversCompany._id;
        newUser.email = undefined;

        const newDriver = new CompanyDriverOperator({
            modelConstructor: User,
            model: plainToClass(User, newUser),
            modules: null,
            data: {
                simpleCountries: this.simpleCountries,
            },
            onSave: async (user: User & { driver: Driver }) => {
                user.countryId = this.driversCompany.countryId;

                user._id = await this.rpcClient.user.createUser(user);
                await this.fetchCompanyDrivers();
            },
            isNew: true,
            validateOptions: { skipMissingProperties: true },
        });

        newDriver.edit(() => {});
        this.companyDriverOperators.unshift(newDriver);
    }

    // data fetching

    @action
    public async onFetchData(): Promise<void> {
        await this.fetchContent();
        await this.fetchCompanyDrivers();
    }

    @action
    public async fetchContent(): Promise<void> {
        this.driversCompany = this.pageRouter.userId
            ? await this.rpcClient.user.retrieveUser(this.pageRouter.userId)
            : null;
        this.simpleCountries = await this.rpcClient.content.retrieveSimpleCountries({});
    }

    @action
    public async fetchCompanyDrivers(): Promise<void> {
        const drivers = await this.rpcClient.driversCompany.retrieveCompanyDrivers(this.pageRouter.userId);
        const documentTypes = plainToClass(
            DocumentType,
            await this.rpcClient.document.retrieveDocumentTypes({
                subject: DocumentSubject.CompanyDriver,
                countryIds: drivers.map((d) => d.countryId!),
            }),
        );

        const companyDriverOperators: Array<CompanyDriverOperator> = [];

        drivers
            .filter(
                (driver) =>
                    isUndefinedOrNull(this.pageRouter.search) ||
                    driver.firstName.includes(this.pageRouter.search) ||
                    driver.lastName.includes(this.pageRouter.search) ||
                    (isNotUndefinedOrNull(driver.email) && driver.email.includes(this.pageRouter.search)) ||
                    driver.phoneNumber.includes(this.pageRouter.search),
            )
            .forEach((d) => {
                d.driver = plainToClass(Driver, d.driver);

                companyDriverOperators.push(
                    new CompanyDriverOperator({
                        modelConstructor: User,
                        model: d,
                        modules: null,
                        data: {
                            simpleCountries: this.simpleCountries,
                        },
                        onFetchData: async (operator: CompanyDriverOperator) => {
                            operator.model = plainToClass(
                                User,
                                await this.rpcClient.driversCompany.retrieveCompanyDriver(
                                    this.pageRouter.userId,
                                    operator.m._id,
                                ),
                            );

                            if (operator.isEdited()) {
                                operator.editedModel = operator.model;
                            }
                        },
                        async beforeSave(user: User & { driver: Driver }) {
                            throwIfInvalidUserPhoneNumber(user.phoneNumber);
                            throwIfInvalidUserPhoneNumber(user.whatsappNumber, true);
                        },
                        onSave: async (user: User & { driver: Driver }) => {
                            const thisOperator = this.companyDriverOperators.find(
                                (cdo) => cdo.m._id === user._id,
                            ) as CompanyDriverOperator;
                            if (!thisOperator.isAddressValid) {
                                throw new Error("Provided hometown is not valid");
                            }
                            try {
                                if (
                                    this.authenticationStore.isCompanyDriver ||
                                    this.authenticationStore.isDriversCompany
                                ) {
                                    await this.rpcClient.driver.selfUpdateTransporterUser(user._id, user);
                                } else {
                                    await this.rpcClient.user.updateUser(user._id, user, {
                                        validationGroups: [UserValidationGroups.PROFILE, UserValidationGroups.DRIVER],
                                    });
                                }
                                const thisOperator = this.companyDriverOperators.find(
                                    (cdo) => cdo.m._id === d._id,
                                ) as CompanyDriverOperator;
                                thisOperator.fetchData();
                            } catch (e: any) {
                                globalManagementLogger.error(e);
                                throw e;
                            }
                        },
                        documentTypeOperators: documentTypes
                            .map((docType: DocumentType) => {
                                const document = (d.driver as Driver).documents.find(
                                    (doc) => doc.documentTypeId == docType._id && isUndefinedOrNull(doc.deletedAt),
                                );

                                const dto = new DocumentTypeOperator({
                                    modelConstructor: DocumentType,
                                    model: docType,
                                    documentOperator: isNotUndefinedOrNull(document)
                                        ? new DocumentOperator({
                                              modelConstructor: Document,
                                              model: document,
                                              data: null,
                                              modules: null,
                                          })
                                        : undefined,
                                    data: null,
                                    modules: null,
                                    userId: d._id,
                                    isMangopayInformationsFilled: false,
                                    onDriverDocumentUploaded: async () => {
                                        const thisOperator = this.companyDriverOperators.find(
                                            (cdo) => cdo.m._id === d._id,
                                        ) as CompanyDriverOperator;

                                        thisOperator.cancelEdit();
                                        await this.fetchCompanyDrivers();
                                    },
                                    onDriversCompanyDocumentUploaded: async () => {
                                        const thisOperator = this.companyDriverOperators.find(
                                            (cdo) => cdo.m._id === d._id,
                                        ) as CompanyDriverOperator;

                                        thisOperator.cancelEdit();
                                        await this.fetchCompanyDrivers();
                                    },
                                });

                                // make sure selfie video document type is not displayed for old active drivers
                                // which been active before the selfie video document type was added
                                if (dto.isSelfieVideo && d.driver.status === DriverStatus.Active && !document) {
                                    return undefined;
                                }

                                return dto;
                            })
                            .filter((dto) => isNotUndefinedOrNull(dto)),
                        validateOptions: { skipMissingProperties: true },
                    }),
                );
            });

        this.companyDriverOperators = companyDriverOperators;
    }

    public isDataFetched(): this is CompanyDriversPageStore & CompanyDriversPageStoreDataFetched {
        return isNotUndefinedOrNull(this.driversCompany) && isNotUndefinedOrNull(this.companyDriverOperators);
    }
}

export interface CompanyDriversPageStoreDataFetched {
    driversCompany: User;
    simpleCountries: Array<SimpleCountry>;
    companyDriverOperators: Array<CompanyDriverOperator>;
}
